// CONTRACT:C4-DESIGN-TOKENS.1.0
/** Error contracts for the design-token plane.
 *  - ErrMissingToken      (MISSING_TOKEN)       — referenced semantic token undefined in active theme.
 *  - ErrInvalidTheme      (INVALID_THEME)       — theme incomplete/malformed; rejected before activation.
 *  - ErrTokenTypeMismatch (TOKEN_TYPE_MISMATCH) — a value's type ≠ its declared slot type.
 *  These are the SINGLE source of design-token error semantics; resolvers/validators MUST
 *  throw these rather than coercing silently or yielding invisible output. */

/** Stable machine-readable error codes (mirror architecture/CONTRACT-C4-DESIGN-TOKENS.1.0.md). */
export type DesignTokenErrorCode =
  | "MISSING_TOKEN"
  | "INVALID_THEME"
  | "TOKEN_TYPE_MISMATCH";

/** Common base so callers can `instanceof DesignTokenError` and read `.code`. */
export class DesignTokenError extends Error {
  readonly code: DesignTokenErrorCode;
  constructor(code: DesignTokenErrorCode, message: string) {
    super(message);
    this.name = "DesignTokenError";
    this.code = code;
    // Restore prototype chain across the ES target down-level (instanceof safety).
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/** A referenced semantic token is undefined in the active theme. The resolver throws this
 *  and the caller falls back to the documented default for that slot — never invisible output. */
export class ErrMissingToken extends DesignTokenError {
  /** The semantic token key that was requested but not present. */
  readonly tokenKey: string;
  /** The theme id the lookup was attempted against. */
  readonly themeId: string;
  constructor(tokenKey: string, themeId: string) {
    super(
      "MISSING_TOKEN",
      `Token "${tokenKey}" is not defined in theme "${themeId}".`,
    );
    this.name = "ErrMissingToken";
    this.tokenKey = tokenKey;
    this.themeId = themeId;
  }
}

/** A theme is incomplete or malformed; rejected before activation so the previous theme stays active. */
export class ErrInvalidTheme extends DesignTokenError {
  /** The theme id that failed validation (may be empty/unknown for malformed input). */
  readonly themeId: string;
  /** Per-slot reasons the theme was rejected (missing slots, mismatches, etc.). */
  readonly reasons: readonly string[];
  constructor(themeId: string, reasons: readonly string[]) {
    super(
      "INVALID_THEME",
      `Theme "${themeId}" is invalid: ${reasons.join("; ")}`,
    );
    this.name = "ErrInvalidTheme";
    this.themeId = themeId;
    this.reasons = reasons;
  }
}

/** A token value's type does not match its declared slot type (e.g. a number where a color is required). */
export class ErrTokenTypeMismatch extends DesignTokenError {
  /** The semantic token key whose value was the wrong type. */
  readonly tokenKey: string;
  /** The declared slot type the value should have matched. */
  readonly expected: SlotType;
  /** The value actually supplied (for diagnostics). */
  readonly received: unknown;
  constructor(tokenKey: string, expected: SlotType, received: unknown) {
    super(
      "TOKEN_TYPE_MISMATCH",
      `Token "${tokenKey}" expected type "${expected}" but received ${describeValue(received)}.`,
    );
    this.name = "ErrTokenTypeMismatch";
    this.tokenKey = tokenKey;
    this.expected = expected;
    this.received = received;
  }
}

/** Declared types for semantic slots — each slot has a type so values are never coerced silently. */
export type SlotType = "color" | "length" | "duration" | "easing" | "fontFamily" | "fontSize";

function describeValue(v: unknown): string {
  if (v === null) return "null";
  if (typeof v === "string") return `string ${JSON.stringify(v)}`;
  return `${typeof v} (${String(v)})`;
}
